import {
  emptyModelForm as emptyTextModelForm,
  modelFormFromRow as textModelFormFromRow,
  toModelSaveBody as textModelSaveBody,
} from './llm-model-form.js'

export function newCapabilityModelForm(capability, providerCapabilityId) {
  if (capability === 'text') {
    return { capability, provider_capability_id: providerCapabilityId, ...emptyTextModelForm() }
  }
  return {
    capability,
    provider_capability_id: providerCapabilityId,
    model: '',
    enabled: capability !== 'tts',
    image_price: '',
    video_with_input_price: '',
    video_without_input_price: '',
    tts_price: '',
    embedding_price: '',
    dimensions: '',
    params: '',
  }
}

export function capabilityModelFormFromRow(capability, row) {
  if (capability === 'text') {
    return {
      capability,
      provider_capability_id: row.provider_capability_id,
      ...textModelFormFromRow({ ...row, enabled: row.enabled === 1 }),
    }
  }
  const form = newCapabilityModelForm(capability, row.provider_capability_id)
  form.model = row.model
  form.enabled = row.enabled === 1
  form.params = row.params === null ? '' : JSON.stringify(row.params, null, 2)
  switch (capability) {
    case 'image':
      form.image_price = yuanFromMicros(row.pricing.price_micros_per_image)
      return form
    case 'video':
      form.video_with_input_price = yuanFromMicros(row.pricing.with_video_input_micros_per_m)
      form.video_without_input_price = yuanFromMicros(row.pricing.without_video_input_micros_per_m)
      return form
    case 'tts':
      form.tts_price = yuanFromMicros(row.pricing.price_micros_per_10k)
      return form
    case 'embedding':
      form.dimensions = String(row.dimensions)
      // 向量只有输入，价目就是 token_tiered 的单档输入价。
      form.embedding_price = yuanFromMicros(row.pricing.tiers[0].input_micros_per_m)
      return form
    default:
      throw new Error(`Unhandled model capability: ${capability}`)
  }
}

export function capabilityModelSaveBody(form) {
  if (form.capability === 'text') {
    return {
      provider_capability_id: form.provider_capability_id,
      ...textModelSaveBody(form),
    }
  }
  const errors = validateCapabilityModelForm(form)
  if (Object.keys(errors).length > 0) {
    throw new Error(`invalid capability model form fields: ${Object.keys(errors).join(', ')}`)
  }
  const body = {
    model: form.model.trim(),
    provider_capability_id: form.provider_capability_id,
    pricing: pricingFromForm(form),
    params: paramsFromText(form.params),
    enabled: form.enabled,
  }
  // 向量模型独有：维度是这一行的向量空间，服务端要求必填且建后不可改。
  if (form.capability === 'embedding') body.dimensions = Number(form.dimensions)
  return body
}

export function validateCapabilityModelForm(form) {
  if (form.capability === 'text') throw new Error('validateCapabilityModelForm only accepts non-text models')
  const errors = {}
  if (form.model.trim() === '') errors.model = 'required'
  else if (form.model.length > 128) errors.model = 'tooLong'
  const params = parseParams(form.params)
  if (params.error) errors.params = params.error
  if (form.capability === 'embedding' && !isPositiveInteger(form.dimensions)) errors.dimensions = 'positiveInt'
  for (const field of pricingFields(form.capability)) {
    if (!isNonNegativePrice(form[field])) errors[field] = 'nonNegPrice'
  }
  return errors
}

function pricingFromForm(form) {
  switch (form.capability) {
    case 'image':
      return { mode: 'image', price_micros_per_image: microsFromYuan(form.image_price) }
    case 'video':
      return {
        mode: 'video_token',
        with_video_input_micros_per_m: microsFromYuan(form.video_with_input_price),
        without_video_input_micros_per_m: microsFromYuan(form.video_without_input_price),
      }
    case 'tts':
      return { mode: 'character', price_micros_per_10k: microsFromYuan(form.tts_price) }
    case 'embedding': {
      // 向量调用只有输入，没有输出、也没有缓存差价：四个价位填同一个数，
      // 记账那边（calculateModelCost）按 token_tiered 算出来就等于「输入 token × 单价」。
      const price = microsFromYuan(form.embedding_price)
      return {
        mode: 'token_tiered',
        tiers: [{
          input_up_to: null,
          input_micros_per_m: price,
          output_micros_per_m: 0,
          cached_input_micros_per_m: price,
          cache_write_input_micros_per_m: price,
        }],
      }
    }
    default:
      throw new Error(`Unhandled model capability: ${form.capability}`)
  }
}

function microsFromYuan(value) {
  return Math.round(Number(value) * 1_000_000)
}

function yuanFromMicros(value) {
  return String(Number(value) / 1_000_000)
}

function pricingFields(capability) {
  switch (capability) {
    case 'image':
      return ['image_price']
    case 'video':
      return ['video_with_input_price', 'video_without_input_price']
    case 'tts':
      return ['tts_price']
    case 'embedding':
      return ['embedding_price']
    default:
      throw new Error(`Unhandled model capability: ${capability}`)
  }
}

function isPositiveInteger(value) {
  return /^[1-9]\d*$/.test(String(value).trim())
}

function parseParams(text) {
  if (text.trim() === '') return { value: null }
  try {
    const value = JSON.parse(text)
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
      return { value: null, error: 'notObject' }
    }
    return { value }
  } catch {
    return { value: null, error: 'invalidJson' }
  }
}

function paramsFromText(text) {
  return parseParams(text).value
}

function isNonNegativePrice(value) {
  return /^\d+(\.\d{1,6})?$/.test(value.trim())
}
